// mobile-search — lógica PURA da busca mobile (parede A6).
//
// Mesmo padrão de mobile-shell-view.ts / mobile-catalog.ts: funções puras,
// testáveis sem DOM. A apresentação (MobileSearch) só LIGA estas funções.
//
// REUSO: a BUSCA em si é a MESMA da TV — api.search() -> backend /catalog/search
// (pg_trgm + ranking do search-core). Aqui só tratamos a apresentação touch:
// debounce, filtro por tipo (Tudo/Canais/Filmes/Séries), montagem dos itens
// pro grid/lista mobile e os estados (idle/sem-resultado).

import type { LiveChannel, Series, VodItem } from '@app-iptv/iptv-client'

/** Mínimo de letras pra disparar a busca (bate com a SearchScreen da TV). */
export const MIN_QUERY = 2
/** Debounce do input (ms). A TV usa 500ms (remoto). No celular o teclado é
 *  nativo e a digitação é mais rápida, então 350ms dá feedback mais ágil sem
 *  martelar o backend (que ainda cacheia 60s). */
export const SEARCH_DEBOUNCE_MS = 350

/** Filtro por tipo no topo (espelha o chip da TV). */
export type SearchFilterType = 'all' | 'channels' | 'movies' | 'series'

/** kind do backend pro filtro de tipo (api.search recebe este). */
export function kindForFilter(type: SearchFilterType): 'all' | 'vod' | 'series' | 'channels' {
  switch (type) {
    case 'movies':
      return 'vod'
    case 'series':
      return 'series'
    case 'channels':
      return 'channels'
    default:
      return 'all'
  }
}

/** Busca habilitada (query com tamanho mínimo, já trimada). */
export function isSearchEnabled(query: string): boolean {
  return query.trim().length >= MIN_QUERY
}

/** Item normalizado pro grid/lista mobile (VOD/série) ou linha de canal. */
export interface SearchResultItem {
  id: string
  kind: 'vod' | 'series' | 'channel'
  title: string
  poster: string | null
  year?: number | null | undefined
}

/** Subconjunto da resposta de busca (api.search) que a tela mobile consome. */
export interface SearchData {
  channels: LiveChannel[]
  vod: VodItem[]
  series: Series[]
}

/** Total de hits considerando o filtro de tipo. */
export function totalHits(data: SearchData, type: SearchFilterType): number {
  return buildResultItems(data, type).length
}

/**
 * Monta a lista de itens a renderizar conforme o filtro de tipo. Ordem em
 * "Tudo": canais, filmes, séries (mesma ordem visual da TV). Itens sem id são
 * pulados (defensivo contra dados sujos do Xtream).
 */
export function buildResultItems(data: SearchData, type: SearchFilterType): SearchResultItem[] {
  const out: SearchResultItem[] = []
  const showChannels = type === 'all' || type === 'channels'
  const showMovies = type === 'all' || type === 'movies'
  const showSeries = type === 'all' || type === 'series'

  if (showChannels) {
    for (const c of data.channels) {
      if (!c.id) continue
      out.push({ id: c.id, kind: 'channel', title: c.name, poster: c.logo ?? null })
    }
  }
  if (showMovies) {
    for (const m of data.vod) {
      if (!m.id) continue
      out.push({ id: m.id, kind: 'vod', title: m.name, poster: m.poster ?? null, year: m.year })
    }
  }
  if (showSeries) {
    for (const s of data.series) {
      if (!s.id) continue
      out.push({ id: s.id, kind: 'series', title: s.name, poster: s.poster ?? null })
    }
  }
  return out
}

/** Chips de tipo a exibir (só os que têm hits, + "Tudo" sempre quando há algo).
 *  Espelha a SearchScreen da TV (chip por tipo com contagem). */
export interface SearchTypeChip {
  type: SearchFilterType
  count: number
}

export function buildTypeChips(data: SearchData): SearchTypeChip[] {
  const total = data.channels.length + data.vod.length + data.series.length
  if (total === 0) return []
  const chips: SearchTypeChip[] = [{ type: 'all', count: total }]
  if (data.channels.length > 0) chips.push({ type: 'channels', count: data.channels.length })
  if (data.vod.length > 0) chips.push({ type: 'movies', count: data.vod.length })
  if (data.series.length > 0) chips.push({ type: 'series', count: data.series.length })
  return chips
}
